import React, { useState, useEffect, useCallback } from 'react';
import { Grid, ChevronDown } from 'lucide-react';
import api from '../../api';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const PERIODS = ['Period 1', 'Period 2', 'Period 3', 'Break', 'Period 4', 'Period 5', 'Period 6', 'Period 7'];
const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri' };

export default function TeacherTimetablePage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/academics/my-assignments').then(r => {
      const seen = new Set();
      const cls = [];
      for (const a of (r.data || [])) {
        if (!seen.has(a.class_id)) {
          seen.add(a.class_id);
          cls.push({ id: a.class_id, name: a.class_name });
        }
      }
      // Also include class_teacher_for class if not already listed
      if (r.class_teacher_for && !seen.has(r.class_teacher_for.id)) {
        cls.unshift(r.class_teacher_for);
      }
      setClasses(cls);
      if (cls.length > 0) setSelectedClass(cls[0]);
    }).catch(() => {});
  }, []);

  const loadTimetable = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const d = await api.get(`/timetable/class/${selectedClass.id}`);
      if (d.success) setSlots(d.data);
    } catch {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass]);

  useEffect(() => { loadTimetable(); }, [loadTimetable]);

  const getSlot = (day, period) => slots.find(s => s.day === day && s.period === period);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="teacher-timetable-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Class Timetable</h1>
          <p className="text-sm text-slate-500 mt-0.5">View your assigned classes' weekly schedule</p>
        </div>
        <div className="relative">
          <select
            value={selectedClass?.id || ''}
            onChange={e => setSelectedClass(classes.find(c => c.id === e.target.value) || null)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white pr-8 appearance-none focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="teacher-class-select"
          >
            <option value="">Select a class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {!selectedClass ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Grid className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-medium text-slate-500">{classes.length === 0 ? 'No classes assigned yet — contact your admin' : 'Select a class to view its timetable'}</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Grid className="w-4 h-4 text-indigo-500" />
            <span className="font-semibold text-slate-700 text-sm">{selectedClass.name} — Weekly Schedule</span>
          </div>
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 w-24">Period</th>
                {DAYS.map(d => (
                  <th key={d} className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500">{DAY_SHORT[d]}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {PERIODS.map(period => (
                <tr key={period} className={period === 'Break' ? 'bg-amber-50' : ''}>
                  <td className="px-4 py-3 font-medium text-xs text-slate-500 whitespace-nowrap">
                    {period === 'Break' ? '☕ Break' : period}
                  </td>
                  {DAYS.map(day => {
                    const slot = getSlot(day, period);
                    if (period === 'Break') {
                      return <td key={day} className="px-3 py-3 text-center text-xs text-amber-500">Break</td>;
                    }
                    return (
                      <td key={day} className="px-2 py-1.5">
                        {slot?.subject_name ? (
                          <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-2 text-xs">
                            <div className="font-semibold text-indigo-700 truncate">{slot.subject_name}</div>
                            {slot.teacher_name && (
                              <div className="text-indigo-400 text-[10px] truncate mt-0.5">{slot.teacher_name}</div>
                            )}
                            {slot.start_time && (
                              <div className="text-slate-400 text-[10px]">{slot.start_time}{slot.end_time ? `–${slot.end_time}` : ''}</div>
                            )}
                          </div>
                        ) : (
                          <div className="min-h-[52px] rounded-lg bg-slate-50 border border-dashed border-slate-200" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
